// @ts-nocheck
import * as React from 'react';
import Icon from '../../components/common/Icon';
import { PageHero, Section, SendBtn, PresentBtn, CompetitivePath, SentBadge } from '../shared/InternalShared';

// inspire365.jsx — Inspire 365 internal page. Accent: violet.
// "Twelve kinds of greatness. One daily habit."

import { useEffect, useState } from "react";
import { fetchInspireItems } from '../services/internalContentApi';

export default function PageInspire365({ student, accent, sendToParent, onContentSelect }) {
  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [preview, setPreview] = useState(null);
  const [contentState, setContentState] = useState({ loading: true, error: '' });
  const [playing, setPlaying] = useState(false);
  const activeMentor = items[selectedIndex] || null;
  const first = student.child.split(" ")[0];
  useEffect(() => { onContentSelect?.(activeMentor); }, [activeMentor, onContentSelect]);

  useEffect(() => {
    let current = true;
    setContentState({ loading: true, error: '' });
    setSelectedIndex(0);
    setPreview(null);
    fetchInspireItems({ age: student.childAge })
      .then((data) => { if (current) setItems(data); })
      .catch((error) => { if (current) { setItems([]); setContentState({ loading: false, error: error?.message || 'Unable to load Inspire 365 content.' }); } })
      .finally(() => { if (current) setContentState((state) => ({ ...state, loading: false })); });
    return () => { current = false; };
  }, [student.childAge]);

  const player = (
    <div className="ip-player ip-rise">
      <div className="ip-player__top">
        <div className="ip-player__art">365</div>
        <div className="ip-player__meta">
          <div className="ip-player__kicker">Today's mindset session · for {first}</div>
          <div className="ip-player__title">Steel Mindset — befriending the fear of maths</div>
          <div className="ip-player__by">In the voice of Dr. APJ Abdul Kalam · {student.language}</div>
        </div>
        <button className="ip-play" onClick={() => setPlaying((p) => !p)} title={playing ? "Pause" : "Play"}>
          {playing
            ? <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
            : <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="7 4 20 12 7 20 7 4"/></svg>}
        </button>
      </div>
      <div className={"ip-wave" + (playing ? " is-playing" : "")}>
        {[...Array(34)].map((_, i) => (
          <span key={i} style={{ height: `${24 + Math.abs(Math.sin(i * 1.3)) * 76}%`, animationDelay: `${i * 0.045}s` }} />
        ))}
      </div>
      <div className="ip-player__bar"><i style={{ width: playing ? "46%" : "32%" }} /></div>
      <div className="ip-player__time"><span>{playing ? "1:24" : "0:58"}</span><span>3:00</span></div>
    </div>
  );

  return (
    <div className="ip" style={{ "--accent": accent }}>
      <PageHero
        eyebrow="Inspire 365 · AI Mentors"
        icon="sparkles"
        title={<>Twelve kinds of greatness.<br/><span className="ip-accent">One daily habit.</span></>}
        lead={`Every morning, ${first} meets a mentor who has already overcome what they fear today — building the resilient mindset top competitive exams reward.`}
        actions={<>
          <SendBtn onClick={() => sendToParent("Today's Inspire 365 session", activeMentor)} label="Send today's session" />
          <PresentBtn onClick={() => setPlaying(true)} label="Play in demo" />
        </>}
        trust={{ label: "Inspired by", items: [
          { label: "NEP 2020" },
          { label: "Dr. APJ Abdul Kalam" },
        ] }}
        right={player}
      />

      <Section
        eyebrow="The mentor library"
        title="A role model for every kind of strength"
        hint={`Personalised for ${first} · age ${student.childAge}`}
      >
        {contentState.loading ? <CardSkeletonGrid /> : null}
        {contentState.error ? <div className="ip-api-state" role="alert">{contentState.error}</div> : null}
        {!contentState.loading && !contentState.error && !items.length ? <div className="ip-api-state">No role models are available for this age yet.</div> : null}
        <div className="ip-grid ip-grid--3">
          {items.map((item, index) => (
            <article
              key={`${item.CATEGORY}-${item.TITLE}-${index}`}
              className={"ip-card ip-card--hover ip-api-card" + (selectedIndex === index ? " is-selected" : "")}
              onClick={() => setSelectedIndex(index)}
              tabIndex={0}
              role="button"
              aria-pressed={selectedIndex === index}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelectedIndex(index); } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <div className="ip-iconbox" style={{ width: 34, height: 34, borderRadius: 9 }}><Icon name="sparkles" size={17} /></div>
                <span className="ip-tag">{item.CATEGORY}</span>
                {item.IS_SENT ? <SentBadge /> : null}
              </div>
              <div className="ip-api-card__message">{item.TITLE}</div>
              <div className="ip-preview-actions">
                {item.AUDIO_URL ? <button type="button" className="ip-btn ip-btn--ghost ip-btn--sm" onClick={(event) => { event.stopPropagation(); setSelectedIndex(index); setPreview({ type: 'audio', url: item.AUDIO_URL, title: item.TITLE }); }}>Audio preview</button> : null}
                {item.VIDEO_URL ? <button type="button" className="ip-btn ip-btn--ghost ip-btn--sm" onClick={(event) => { event.stopPropagation(); setSelectedIndex(index); setPreview({ type: 'video', url: item.VIDEO_URL, title: item.TITLE }); }}>Video preview</button> : null}
              </div>
            </article>
          ))}
        </div>
      </Section>

      {/* Selected mentor detail */}
      {activeMentor ? <Section style={{ paddingTop: 0 }}>
        <div className="ip-card ip-rise" key={`${activeMentor.CATEGORY}-${activeMentor.TITLE}`} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "center", padding: "22px 24px", background: "var(--bg)" }}>
          <div>
            <span className="ip-tag">{activeMentor.CATEGORY}</span>
            <h3 style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.02em", margin: "12px 0 4px", color: "var(--fg)", fontFamily: "var(--font-display)" }}>{activeMentor.TITLE}</h3>
            <p style={{ fontSize: 13.5, color: "var(--fg-secondary)", margin: 0, lineHeight: 1.6, maxWidth: 560 }}>
              This age-matched story introduces {first} to a role model whose choices make courage, curiosity and discipline easier to understand.
            </p>
          </div>
          <button className="ip-btn ip-btn--ghost ip-btn--sm" onClick={() => sendToParent(activeMentor.TITLE + " mentor preview", activeMentor)}>
            <Icon name="arrow-right" size={15} /> Add to demo
          </button>
        </div>
      </Section> : null}

      {preview ? <MediaPreview preview={preview} onClose={() => setPreview(null)} /> : null}

      <Section
        eyebrow="Why it compounds"
        title="A daily habit that exams reward"
        style={{ paddingTop: 4 }}
      >
        <div className="ip-grid ip-grid--3">
          {[
            { t: "Daily mental conditioning", d: "Three calm minutes before school — the mindset rep that sets the day.", ic: "sparkles" },
            { t: "Resilience that holds", d: "Borrowed courage from people who failed first, then changed the world.", ic: "shield" },
            { t: "Built for the long race", d: "The same grit Boards, Olympiads, JEE and NEET quietly demand.", ic: "growth" },
          ].map((b) => (
            <div key={b.t} className="ip-card" style={{ padding: 20 }}>
              <div className="ip-iconbox"><Icon name={b.ic} size={20} /></div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg)", marginTop: 14, letterSpacing: "-0.01em" }}>{b.t}</div>
              <div style={{ fontSize: 13, color: "var(--fg-secondary)", marginTop: 6, lineHeight: 1.55 }}>{b.d}</div>
            </div>
          ))}
        </div>
      </Section>

      <div className="ip-band" style={{ "--accent": accent }}>
        <span className="ip-band__eyebrow">Path to competitive success</span>
        <div className="ip-band__title">From a daily story to a steady mind under pressure.</div>
        <div style={{ marginTop: 22, position: "relative" }}>
          <CompetitivePath />
        </div>
      </div>
    </div>
  );
}

function MediaPreview({ preview, onClose }) {
  return <div className="ip-preview-backdrop" role="dialog" aria-modal="true" aria-label={`${preview.type} preview`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className={`ip-preview-modal ip-preview-modal--${preview.type}`}><div className="ip-preview-modal__head"><div className="ip-preview-modal__identity"><span className="ip-preview-modal__type">{preview.type}</span><strong>{preview.title}</strong><small>Personalised content preview</small></div><button type="button" onClick={onClose} aria-label="Close preview">×</button></div><div className="ip-preview-modal__stage">{preview.type === 'audio' ? <><div className="ip-preview-modal__audio-art"><Icon name="voice" size={30} /></div><audio controls autoPlay src={preview.url} /></> : <video controls autoPlay playsInline src={preview.url} />}</div><div className="ip-preview-modal__foot"><span>Preview only</span><small>Use the player controls to adjust volume or playback.</small></div></div></div>;
}

function CardSkeletonGrid() {
  return <div className="ip-card-skeleton-grid" role="status" aria-label="Loading personalised role models">{Array.from({ length: 6 }).map((_, index) => <div className="ip-card ip-card-skeleton" key={index}><span className="ip-skeleton ip-card-skeleton__icon" /><span className="ip-skeleton ip-card-skeleton__tag" /><span className="ip-skeleton ip-card-skeleton__line" /><span className="ip-skeleton ip-card-skeleton__line ip-card-skeleton__line--short" /><span className="ip-skeleton ip-card-skeleton__action" /></div>)}</div>;
}




